import React, { useState, useRef, useEffect } from 'react'
import { NodeViewWrapper } from '@tiptap/react'
import { useTheme } from '@mui/styles'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
} from '@mui/material'
import type { NodeViewProps } from '@tiptap/react'
import type ThemeInterface from '@/theme-interface'
import lang from '@/lang'

export const MjmlButtonNodeView: React.FC<NodeViewProps> = ({
  node,
  getPos,
  editor,
  updateAttributes,
}) => {
  const { text, href, mjClass, cssClass } = node.attrs
  const theme = useTheme<ThemeInterface>()
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editText, setEditText] = useState(text || 'Button')
  const [editHref, setEditHref] = useState(href || '#')
  const [editMjClass, setEditMjClass] = useState(mjClass || '')
  const [editCssClass, setEditCssClass] = useState(cssClass || '')
  const buttonRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setEditText(text || 'Button')
    setEditHref(href || '#')
    setEditMjClass(mjClass || '')
    setEditCssClass(cssClass || '')
  }, [text, href, mjClass, cssClass])

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (typeof getPos === 'function') {
      const pos = getPos()
      editor.commands.setNodeSelection(pos)
    }

    setIsEditDialogOpen(true)
  }

  const handleSave = () => {
    updateAttributes({
      text: editText,
      href: editHref,
      mjClass: editMjClass,
      cssClass: editCssClass,
    })

    setIsEditDialogOpen(false)
  }

  const handleCancel = () => {
    // Reset form values to current node values
    setEditText(text || 'Button')
    setEditHref(href || '#')
    setEditMjClass(mjClass || '')
    setEditCssClass(cssClass || '')
    setIsEditDialogOpen(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSave()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      handleCancel()
    }
  }

  return (
    <NodeViewWrapper>
      <div
        ref={buttonRef}
        data-mjml-button="true"
        data-href={href || '#'}
        data-mj-class={mjClass || undefined}
        data-css-class={cssClass || undefined}
        onClick={handleClick}
        onMouseDown={handleClick}
        contentEditable={false}
        style={{
          display: 'block',
          padding: '10px 20px',
          margin: '5px 0',
          border: `1px solid ${theme.primary}`,
          color: theme.primary,
          borderRadius: '4px',
          textDecoration: 'none',
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          minWidth: '100px',
          position: 'relative',
          backgroundColor: 'transparent',
        }}
      >
        {text || 'Button'}
      </div>

      {/* Edit Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {lang.htmlInput.extensions.mjmlButton.edit.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label={lang.htmlInput.extensions.mjmlButton.edit.label}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={handleKeyDown}
              fullWidth
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>
            {lang.generics.cancel}
          </Button>
          <Button onClick={handleSave} variant="contained">
            {lang.generics.save}
          </Button>
        </DialogActions>
      </Dialog>
    </NodeViewWrapper>
  )
}
